import {storeToRefs} from 'pinia';
import type {ComputedRef} from 'vue';
import type {RouteLocationAsRelativeGeneric, RouteLocationResolvedGeneric} from 'vue-router';
import {compact, flow, join, split, unique, unshift} from '@dovca/toolbox';
import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
import {GLOBAL_BRANCH_SLUG} from '#shared/constants';

type Options = {
	resolve?: true | Extract<keyof RouteLocationResolvedGeneric, 'path' | 'fullPath'>,
};

/**
 * Composable that creates branch-specific paths by prepending the selected hypermarket branch
 * to paths or route objects. This enables dynamic routing based on the currently selected
 * hypermarket branch from the Storyblok hypermarkets store.
 *
 * @example
 * // Basic string path usage
 * const branchPath = useBranchSpecificPath();
 * const productPath = branchPath('products'); // Returns computed('/praha-cakovice/products')
 *
 * // Location object usage
 * const routeObject = branchPath({
 *   name: 'services-detail',
 *   params: { id: '123' }
 * }); // Returns { name: 'branch-services-detail', params: { branch: 'praha-cakovice', id: '123' } }
 *
 * // Resolved route object usage
 * const resolvedRoute = branchPath({
 *   name: 'services-detail',
 *   params: { id: '123' }
 * }, { resolve: true | 'path' | 'fullPath' }); // Returns a resolved route object or string based on the input
 *
 * @returns A function that accepts a path (string or route object) and returns a computed
 * branch-specific path that automatically updates when the selected hypermarket changes
 */

export const useBranchSpecificPath = () => {
	const {selectedHypermarket} = storeToRefs(useStoryblokHypermarketsStore());
	const selectedHypermarketWithFallback = computed(() => selectedHypermarket.value ?? GLOBAL_BRANCH_SLUG);
	const router = useRouter();

	const createRouteFromLocationObject = ({name, params, ...props}: RouteLocationAsRelativeGeneric, {resolve}: Options = {}): RouteLocationAsRelativeGeneric | RouteLocationResolvedGeneric | string => {
		let branchSpecificName: string = 'branch';

		if (name && typeof name === 'string') {
			// Transform route name to branch-specific format:
			// e.g., "services-detail" → "branch-services-detail"
			// Split by hyphens, prepend 'branch', remove duplicates, rejoin
			branchSpecificName = flow(
				name as string,
				split('-'),
				unshift('branch'),
				unique,
				compact,
				join('-'),
			);
		}

		const branchSpecificParams = {branch: selectedHypermarketWithFallback.value, ...params};

		const locationRoute = {
			name: branchSpecificName,
			params: branchSpecificParams,
			...props,
		};

		if (!resolve) {
			return locationRoute;
		}

		if (resolve === true) {
			return router.resolve(locationRoute);
		} else {
			return router.resolve(locationRoute)[resolve];
		}
	};

	const createPathFromString = (path: string) => {
		return `/${selectedHypermarketWithFallback.value}${ensureLeadingSlash(path)}`;
	};

	function branchSpecificPath(path: string): ComputedRef<string>;
	function branchSpecificPath(path: RouteLocationAsRelativeGeneric): ComputedRef<RouteLocationAsRelativeGeneric>;
	function branchSpecificPath(path: RouteLocationAsRelativeGeneric, options: {resolve: true}): ComputedRef<RouteLocationResolvedGeneric>;
	function branchSpecificPath(path: RouteLocationAsRelativeGeneric, options: {resolve: Extract<keyof RouteLocationResolvedGeneric, 'path' | 'fullPath'>}): ComputedRef<RouteLocationResolvedGeneric>;
	function branchSpecificPath(path: string | RouteLocationAsRelativeGeneric, options?: Options): ComputedRef<string | RouteLocationAsRelativeGeneric | RouteLocationResolvedGeneric> {
		return computed(() => {
			if (typeof path === 'string') {
				return createPathFromString(path);
			} else {
				return createRouteFromLocationObject(path, options);
			}
		});
	}

	return branchSpecificPath;
};
